/**
 * Unified Button Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef } from "react";
import { cn } from "../utils/cn";
import type { ButtonProps } from "./Button.types";
import { buttonVariants } from "./Button.variants";

// Re-exported so `import { buttonVariants } from "@plerity/ui-core"` (and the
// component barrel `./Button`) keep working after the leaf-module extraction.
export { buttonVariants };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      icon,
      className,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const resolvedLeftIcon = leftIcon ?? icon;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? (
          <span
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        ) : resolvedLeftIcon ? (
          <span className="shrink-0" aria-hidden="true">
            {resolvedLeftIcon}
          </span>
        ) : null}
        {children}
        {!loading && rightIcon && (
          <span className="shrink-0" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
